import React, { useMemo, useState } from 'react'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { useOrderActions } from '../../../utilities/hooks/useOrderActions'

import { adminTransferTickets, getProfileByEmail } from '../../../utilities/api'

import {
  capitalizeString,
  emailPatternMatch,
  formatNumber,
  getSinglarOrPluralWord,
} from '../../../utilities/helpers'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import { Spinner } from '../../LoadingContainer/Spinner'

const canTransfer = (ticket) => !ticket?.wasScanned && !ticket?.exchanged

const transferErrorMessage = (error) => {
  const data = error?.response?.data
  if (typeof data?.message === 'string' && data.message) return data.message
  if (typeof data?.error?.message === 'string' && data.error.message) {
    return data.error.message
  }
  if (typeof data === 'string' && data) return data
  return 'Could not transfer these tickets. Please try again.'
}

export default function TransferTickets({
  order,
  fromEmail,
  selectedTickets = [],
}) {
  const { closeModal } = useModal()
  const orderActions = useOrderActions()

  const [email, setEmail] = useState('')
  const [recipient, setRecipient] = useState(null)
  const [error, setError] = useState('')
  const [lookingUp, setLookingUp] = useState(false)
  const [saving, setSaving] = useState(false)

  const transferableTickets = useMemo(
    () => selectedTickets.filter(canTransfer),
    [selectedTickets]
  )
  const skippedCount = selectedTickets.length - transferableTickets.length

  const returnToProfile = () => {
    if (order) {
      orderActions.openViewProfile(order)
      return
    }
    closeModal()
  }

  const lookupRecipient = async (event) => {
    event.preventDefault()
    setError('')

    const normalized = email.trim().toLowerCase()
    if (!normalized || !emailPatternMatch(normalized)) {
      setError('Enter a valid recipient email')
      return
    }
    if (
      normalized ===
      String(fromEmail || '')
        .trim()
        .toLowerCase()
    ) {
      setError('Tickets are already assigned to this email address')
      return
    }
    if (!transferableTickets.length) {
      setError('Scanned and exchanged tickets cannot be transferred')
      return
    }

    setLookingUp(true)
    try {
      const response = await getProfileByEmail(
        normalized,
        order?.organizationUUID
      )
      const profile = response?.data
      if (!profile?.email) {
        setRecipient({ email: normalized, pendingAccount: true })
        return
      }
      setRecipient(profile)
    } catch (lookupError) {
      if (lookupError?.response?.status === 404) {
        setRecipient({ email: normalized, pendingAccount: true })
        return
      }
      console.error(lookupError)
      setError('Could not look up that account. Please try again.')
    } finally {
      setLookingUp(false)
    }
  }

  const confirmTransfer = async () => {
    setError('')
    setSaving(true)
    try {
      await adminTransferTickets({
        organizationUUID: order?.organizationUUID,
        fromEmail,
        toEmail: recipient.email,
        ticketIds: transferableTickets.map((ticket) => ticket.id),
      })
      returnToProfile()
    } catch (transferError) {
      console.error(transferError)
      setError(transferErrorMessage(transferError))
      setSaving(false)
    }
  }

  const recipientName = capitalizeString(
    `${recipient?.firstName || ''} ${recipient?.lastName || ''}`.trim()
  )

  return (
    <>
      <Modal.Header closeButton className="border-0 pb-0 mb-4">
        <Modal.Title className="h4 text-dark">Transfer tickets</Modal.Title>
      </Modal.Header>
      <div className="header pt-0 mb-2">
        <h2 className="normal">
          Move {formatNumber(transferableTickets.length)}{' '}
          {getSinglarOrPluralWord(transferableTickets.length).toLowerCase()}{' '}
          from {fromEmail} to another account
        </h2>
      </div>
      <Modal.Body className="pb-0">
        <Stack gap={3}>
          {skippedCount > 0 && (
            <Alert variant="warning" className="mb-0">
              {formatNumber(skippedCount)} scanned or exchanged{' '}
              {getSinglarOrPluralWord(skippedCount).toLowerCase()} will stay on
              this account.
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className="mb-0">
              {error}
            </Alert>
          )}
          {recipient ? (
            recipient.pendingAccount ? (
              <p className="mb-0">
                We'll email {recipient.email} a transfer request. They'll be
                asked to create a Blocktickets account so they can receive these
                tickets from your box office and organization. The tickets stay
                on this account until they accept.
              </p>
            ) : (
              <p className="mb-0">
                Transfer {formatNumber(transferableTickets.length)}{' '}
                {getSinglarOrPluralWord(
                  transferableTickets.length
                ).toLowerCase()}{' '}
                to {recipientName || recipient.email} ({recipient.email}). They
                will show up on that account immediately, and the previous
                check-in codes will stop working.
              </p>
            )
          ) : (
            <Form onSubmit={lookupRecipient}>
              <Form.Group controlId="transfer-recipient-email">
                <Form.Label>Recipient email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  autoFocus
                  placeholder="name@email.com"
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={lookingUp || !transferableTickets.length}
                />
              </Form.Group>
            </Form>
          )}
        </Stack>
      </Modal.Body>
      <Modal.Footer className="border-0 btn-group-flex">
        <Button
          className="btn-back"
          variant="outline-light"
          onClick={() => {
            if (recipient) {
              setRecipient(null)
              setError('')
              return
            }
            returnToProfile()
          }}
          disabled={saving || lookingUp}
        >
          Back
        </Button>
        {recipient ? (
          <Button
            disabled={saving || !transferableTickets.length}
            onClick={confirmTransfer}
          >
            {saving ? (
              <Spinner variant="white" size="md" />
            ) : recipient.pendingAccount ? (
              'Send transfer request'
            ) : (
              'Transfer tickets'
            )}
          </Button>
        ) : (
          <Button
            disabled={lookingUp || !transferableTickets.length || !email.trim()}
            onClick={lookupRecipient}
          >
            {lookingUp ? <Spinner variant="white" size="md" /> : 'Continue'}
          </Button>
        )}
      </Modal.Footer>
    </>
  )
}
